<template>
  <div>

    <va-modal
      :value="isOpen"
      id="availabilityFormModal"
      size="medium"
      :noOutsideDismiss="true"
      :showOkay="isCreatingOrUpdatingReport"
      :okText="$t('modals.buttons.submit')"
      :cancelText="isCreatingOrUpdatingReport ? $t('modals.buttons.cancel') : $t('modals.buttons.back')"
      @ok="submit"
      @cancel="onCancel"
      :fixedLayout="true"
    >

      <div slot="header" class="modal-title">
        {{modalTitle}}
      </div>

      <div class="modal-header-actions d-flex justify-content-end">
          <va-popover
            v-if="screening.report_id !== null"
            placement="left"
            :message="$t('agenda.reports.update_report')"
          >
            <va-button
              id="button-update-report"
              large
              class="va-button-rounded"
              icon="fa fa-pencil"
              color="gray"
              :class="{'active': isUpdatingReport}"
              @click="isUpdatingReport = !isUpdatingReport">
            </va-button>
          </va-popover>

          <va-popover
            v-if="screening.report_id !== null"
            placement="left"
            :message="$t('agenda.reports.delete_report')">
            <va-button
              large
              class="va-button-rounded"
              icon="fa fa-trash"
              color="gray"
              @click="isOpen = false; isDestroyingReport = true">
            </va-button>
          </va-popover>
      </div>

      <div v-if="isLoading" class="d-flex align-items-center flex-column py-5">
        <span class="mb-3">{{$t('agenda.reports.loading_report_data')}}</span>
        <hollow-dots-spinner
          :animation-duration="1000"
          :size="60"
          color="#29c4a9"
        />
      </div>

      <div v-else>
        <!-- Session Informations Details -->
        <h4 class="mb-3">{{$t('agenda.reports.general_info')}}</h4>

        <div class="row">

          <div class="col-12 col-sm-6">
            <span class="is-bold">{{$t('agenda.reports.name')}}:</span>
            <p>{{screening.facility ? nameWithCity(screening.facility) : $t('agenda.screenings.no_facility')}}</p>
          </div>

          <div class="col-12 col-sm-6">
            <span class="is-bold">{{$t('agenda.reports.schedule')}}:</span>
            <p>{{screening.start | localTime('YYYY-MM-DD HH:mm') }} / {{screening.end | localTime('YYYY-MM-DD HH:mm') }}</p>
          </div>

          <div class="col-12 col-sm-6">
            <span class="is-bold">{{$t('agenda.reports.promoter')}}:</span>
            <p>{{screening.user ? screening.user.name : $t('agenda.screenings.unassigned_promotor')}}</p>
          </div>

        </div>

        <!-- End Session Informations Details -->
        <hr class="mt-0">

        <!-- Report Questions Details -->
        <div v-if="!isCreatingOrUpdatingReport">
          <div v-for="section in questions" :key="section.id">
            <h4 class="mb-3">{{section.title}}</h4>
            <div class="row">
              <div class="col-6" v-for="question in section.questions" :key="question.id">
                <div>
                  <b class="profile-details-label">{{question.text}}?</b>

                  <p v-if="question.type === 'boolean'">
                    {{question.answer ? $t('agenda.reports.yes') : $t('agenda.reports.no')}}
                  </p>
                  <p v-else>{{question.answer ? question.answer : 'N/A'}}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- End Report Questions Details -->

        <!-- Report Create or Edit Form -->
        <div v-else>
          <form ref="closeSessionForm" class="evollu-form">
            <div v-for="section in questions" :key="section.id" class="mb-4">
              <h4 v-text="section.title"></h4>
              <div class="row">
                <ScreeningReportQuestion
                  v-for="question in section.questions"
                  :key="question.id"
                  :section="section"
                  :question="question"
                  :customClass="'col-12 col-sm-6'"
                  :hasError="formErrors[question.id]" />
              </div>
            </div>
          </form>
        </div>

        <!-- End Report Create Or Edit Form -->

      </div>

    </va-modal>

    <!-- Delete Report Modal Confirmation -->
    <confirmation-modal v-if="isDestroyingReport"
      :title="$t('agenda.reports.delete')"
      :body="$t('agenda.reports.delete_message')"
      @modal-canceled="cancelDestroyReport"
      @modal-confirmed="destroyReport"
    >
    </confirmation-modal>
    <!-- End Delete Report Modal Confirmation -->
  </div>
</template>
<script>
import ScreeningReportQuestion from './ScreeningReportQuestion'
import LoadingAnimation from '@/components/common/LoadingAnimation'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import { ScreeningService, ReportService } from '@/api/BookingService.js'
import { HollowDotsSpinner } from 'epic-spinners'

export default {
  props: {
    screening: {
      type: Object,
      required: true
    }
  },
  components: {
    ScreeningReportQuestion,
    LoadingAnimation,
    ConfirmationModal,
    HollowDotsSpinner
  },
  data () {
    return {
      isLoading: true,
      isOpen: false,
      questions: [],
      formErrors: {},
      isDestroyingReport: false,
      isCreatingReport: false,
      isUpdatingReport: false
    }
  },
  created () {
    if (this.screening.report_id) {
      this.loadSessionReport()
    } else {
      this.isCreatingReport = true
      this.loadSessionQuestions()
    }
  },
  mounted () {
    this.isOpen = true
  },
  methods: {
    destroyReport: function () {
      ReportService.delete(this.screening.id)
        .then(response => {
          this.showToast(this.$t('agenda.reports.delete_success'), this.$store.state.successToastOptions)
          this.$emit('report-deleted', { screening: this.screening })
          this.onCancel()
        }).catch(errors => {
          console.log('errors', errors)
          this.showToast(this.$t('agenda.reports.delete_error'), this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.cancelDestroyReport()
        })
    },
    cancelDestroyReport: function () {
      this.isDestroyingReport = false
      this.isOpen = true
    },
    loadSessionReport: function () {
      ReportService.get(this.screening.id)
        .then((questions) => {
          this.questions = this.parseQuestions(questions)
        }).catch((err) => {
          console.log(err)
        }).finally(() => {
          this.isLoading = false
        })
    },
    loadSessionQuestions: function () {
      ScreeningService.getAnswers(this.screening.id)
        .then(questions => {
          this.questions = this.parseQuestions(questions)
        }).catch((err) => {
          console.log(err)
        }).finally(() => {
          this.isLoading = false
        })
    },
    parseQuestions: function (questions) {
      return questions.map(section => {
        return {
          ...section,
          questions: section.questions.map(question => {
            return {
              ...question,
              answer: question.type === 'boolean' ? (question.answer ?  true : (question.answer !== '' ? parseInt(question.answer) === 1 : false)) : question.answer
            }
          })
        }
      })
    },
    getFormData: function () {
      if (!this.submittableSections.length) return
      let body = { answers: this.submittableSections.map(section => {
        return section.questions.map(question => {
          return {
            id: question.id,
            answer: question.answer
          }
        })
      }).flat() }
      return body
    },
    submit: function () {
      let errors = this.getFormErrors()
      console.log(errors)
      if (Object.keys(errors).length) return
      let data = this.getFormData()
      let form = {
        store: {
          method: 'store',
          args: [this.screening.id, data],
          message: this.$t('agenda.reports.create_success'),
          emit: 'report-created'
        },
        update: {
          method: 'update',
          args: [this.screening.report_id, data],
          message: this.$t('agenda.reports.update_success'),
          emit: 'report-updated'
        }
      }
      let action = this.isCreatingReport ? form.store : form.update
      ReportService[action.method](...action.args)
        .then(report => {
          this.showToast(this.$t(action.message), this.$store.state.successToastOptions)
          this.$emit(action.emit, { report, screening: this.screening })
          this.isCreatingReport = false
          this.isUpdatingReport = false
        }).catch(errors => {
          console.log('errors', errors)
          this.showToast(this.$t('agenda.reports.form_has_errors'), this.$store.state.errorToastOptions)
        })
    },
    getFormErrors: function () {
      this.formErrors = {}
      this.submittableSections.forEach(section => {
        section.questions.forEach(question => {
          this.$set(this.formErrors, question.id, question.required && (question.answer === null || question.answer === undefined || question.answer === '') ? this.$t('agenda.reports.errors.the_field_is_required', { field: question.text.toLowerCase() }) : false)
        })
      })
      return Object.values(this.formErrors).filter(value => value)
    },
    onCancel: function () {
      if (this.isUpdatingReport) {
        this.isUpdatingReport = false
      } else {
        this.$emit('modal-closed')
      }
    },
    nameWithCity ({ name, address }) {
      if (!address) return name
      if (address.city) {
        return `${name} (${address.city})`
      }
      return name
    }
  },
  computed: {
    modalTitle: function () {
      return `${this.screening.facility.name} - ${this.$t('agenda.reports.title')}`
    },
    isCreatingOrUpdatingReport: function () {
      return this.isCreatingReport || this.isUpdatingReport
    },
    submittableSections: function () {
      if (!this.questions) return []
      return this.questions.filter(section => section.id !== 1)
    }
  }
}
</script>

<style lang="scss">
#button-update-report.active {
  background-color: $evollu-dark !important;
}
</style>
